import { useParams } from 'react-router-dom';
import {
  FormGroup, Label, Button, FormFeedback,
} from 'reactstrap';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import {
  useState, ChangeEvent, useEffect, Fragment,
  useCallback,
} from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorMessage } from '@hookform/error-message';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import moment from 'moment';

import InputField from 'src/components/shared/form-fields/InputField';
import AvailabilitySubmittedSuccess from 'src/pages/SelectAvailability/Components/AvailabilitySubmittedSuccess';

import { getFormattedDateMDY, handleErrorMessages, hasDaytimeAvailability } from 'src/helpers/utils';
import { submitResidentAvailabilities, submitResidentAvailabilitiesForesight } from 'src/services/resident-availability';
import { useResidentAvailabilitiesContext } from 'src/components/context/ResidentAvailibilitiesContext';
import { AvailabilitiesFormState } from 'src/helpers/types';

interface FormStateType {
  resident_availabilities: Array<AvailabilitiesFormState>;
}

const timings = [
  {
    label: 'Morning',
    time: '8am-12pm',
    value: 'morning',
    id: '2a95bb3a-dba1-4e89-8fe6-ff73c231fb50',
  },
  {
    label: 'Afternoon',
    time: '12pm-4pm',
    value: 'afternoon',
    id: '7b35a01d-9231-4b62-a47d-18bbcda28541',
  },
  {
    label: 'Evening',
    time: '4pm-8pm',
    value: 'evening',
    id: '5b659dea-ecdf-4973-96bd-04b4d580f75e',
  },
];

const hasAllChecked = (currentTimings: string[]) => timings.every(
  (timing) => currentTimings.includes(timing.value),
);

const getDayValue = (date: Date, timezone: string) => {
  const today = new Date(
    new Date().toLocaleString('en-US', { timeZone: timezone }),
  );
  const tomorrow = new Date(
    new Date().toLocaleString('en-US', { timeZone: timezone }),
  );
  today.setDate(today.getDate());
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isSameDay = (date1: Date, date2: Date) => date1.getDate() === date2.getDate()
    && date1.getMonth() === date2.getMonth()
    && date1.getFullYear() === date2.getFullYear();

  if (isSameDay(date, today)) return 'today';
  if (isSameDay(date, tomorrow)) return 'tomorrow';

  return null;
};

const schema = yup.object({
  resident_availabilities: yup
    .array()
    .of(
      yup.object().shape({
        date: yup.date().required('Date is required'),
        timings: yup.array(),
      }),
    )
    .required()
    .test(
      'at-least-three-with-timings',
      'Choose 3 or more days to proceed',
      (resident_availabilities) => {
        const countWithTimings = resident_availabilities?.filter(
          (availability) => !!availability?.timings?.length,
        )?.length || 0;
        return countWithTimings > 2;
      },
    )
    .test(
      'at-least-one-daytime',
      'Select at least one Morning or Afternoon window to proceed',
      (resident_availabilities) => hasDaytimeAvailability(resident_availabilities),
    ),
});

const SelectResidentAvailability = () => {
  const { tenantAccessCode, tenantContactHash } = useParams();
  const {
    submittedAvailabilities,
    setSubmittedAvailabilities,
  } = useResidentAvailabilitiesContext();

  const generateDates = useCallback(() => {
    let dates:Array<AvailabilitiesFormState> = [];

    if (submittedAvailabilities?.timezone) {
      let currentDate = new Date(
        new Date().toLocaleString('en-US', { timeZone: submittedAvailabilities?.timezone }),
      );
      while (dates?.length !== 8) {
        currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));

        // Exclude Dec 25 (christmas)
        const isChristmasDay = (currentDate?.getDate() === 25
        && currentDate?.getMonth() === 11);

        // Exclude Jan 1 (New year)
        const isNewYearDay = (currentDate?.getDate() === 1
        && currentDate?.getMonth() === 0);

        // Exclude July 4th 2025 (US is Independence Day)
        const isUSIndependenceDay = (currentDate?.getDate() === 4
        && currentDate?.getMonth() === 6);

        // First Monday of September
        const isLaborDay = currentDate.getMonth() === 8
        && currentDate.getDay() === 1
        && currentDate.getDate() >= 1
        && currentDate.getDate() <= 7;

        // Fourth Thursday of November
        const isThanksgivingDay = currentDate.getMonth() === 10
        && currentDate.getDay() === 4
        && currentDate.getDate() >= 22
        && currentDate.getDate() <= 28;

        const excludedDates = [
          isLaborDay,
          isChristmasDay,
          isNewYearDay,
          isUSIndependenceDay,
          isThanksgivingDay,
        ];

        if (
          currentDate.getDay() !== 0
          && currentDate.getDay() !== 6
          && excludedDates?.every((date) => date === false)
        ) {
          dates.push({
            date: new Date(currentDate),
            timings: [],
          });
        }
      }

      // if dates already selected by foresight coordinator then we have to show those dates here
      if (submittedAvailabilities?.is_editable
        && submittedAvailabilities?.resident_availability && dates?.length) {
        const availableDatesMap = new Map(submittedAvailabilities?.resident_availability.map(
          (item) => [item?.date, item],
        ));
        const addedAvailableDate = new Set();

        // Checking whether the date is already available in the submitted availabilities
        // and if it is then we will add its timings to the date
        dates = dates?.map((item) => {
          const formattedDate = moment(item?.date, 'MM-DD-YYYY').format('MM-DD-YYYY');
          if (availableDatesMap.has(formattedDate)) {
            addedAvailableDate.add(formattedDate);
            return {
              ...item,
              timings: availableDatesMap.get(formattedDate)?.timings,
            };
          }
          return item;
        });

        // Appending the submitted available dates to the dates array
        // which are not already added because they are beyond 8 dates from today
        availableDatesMap?.forEach((item) => {
          if (!addedAvailableDate.has(item?.date)) {
            dates.push({
              date: moment(item?.date, 'MM-DD-YYYY').toDate(),
              timings: item?.timings,
            });
          }
        });
      }
    }
    return dates;
  }, [
    submittedAvailabilities?.is_editable,
    submittedAvailabilities?.resident_availability,
    submittedAvailabilities?.timezone,
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const methods = useForm<FormStateType>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    watch,
    getValues,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  const { fields } = useFieldArray({
    control,
    name: 'resident_availabilities',
  });

  const watchFieldArray = watch('resident_availabilities');

  const controlledFields = fields?.map((field, index) => ({
    ...field,
    ...watchFieldArray?.[index],
  }));

  const onAvailabilityChangeHandler = (
    index: number,
    event: ChangeEvent<HTMLInputElement>,
    selectedValue: string,
  ) => {
    let selectedTimings = getValues(`resident_availabilities.${index}.timings`);
    if (event?.target?.checked) {
      selectedTimings = [...(selectedTimings || []), selectedValue];
    } else {
      selectedTimings = selectedTimings?.filter(
        (item) => item !== selectedValue,
      );
    }
    return selectedTimings;
  };

  const onChangeAnytimeHandler = (
    index: number,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    let selectedTimings = getValues(`resident_availabilities.${index}.timings`);
    if (event?.target?.checked) {
      selectedTimings = ['morning', 'afternoon', 'evening'];
    } else {
      selectedTimings = [];
    }
    return selectedTimings;
  };

  const onSubmit = useCallback((formData: FormStateType) => {
    if (tenantAccessCode) {
      setIsSubmitting(true);
      const formattedPayload = {
        resident_availability: formData?.resident_availabilities
          ?.map((availability) => ({
            ...availability,
            date: getFormattedDateMDY(availability?.date)?.replace(/\//g, '-'),
          }))
          .filter((item) => (item?.timings || []).length > 0),
        ...(tenantContactHash ? { tenant_code: tenantContactHash } : {}),
      };

      const geSubmitApiEndpoint = () => {
        if (import.meta.env.VITE_PLATFORM === 'lula') {
          return submitResidentAvailabilities(tenantAccessCode, formattedPayload);
        }
        // for foresight
        return submitResidentAvailabilitiesForesight(tenantAccessCode, formattedPayload);
      };

      geSubmitApiEndpoint()
        .then((response) => {
          setIsSubmitting(false);
          toast(response?.message || 'Resident availability submitted successfully.', { type: 'success', theme: 'colored' });
          setSubmittedAvailabilities((prev) => ({
            ...prev,
            resident_availability: formattedPayload?.resident_availability,
            is_editable: false,
          }));
        })
        .catch((error) => {
          setIsSubmitting(false);
          if (error?.data?.message) {
            toast(error?.data?.message, { type: 'error', theme: 'colored' });
          } else {
            handleErrorMessages(
              {
                errors: error?.data?.errors,
              },
              (msg) => toast(msg, { type: 'error', theme: 'colored' }),
            );
          }
        });
    }
  }, [setSubmittedAvailabilities, tenantAccessCode, tenantContactHash]);

  useEffect(() => {
    const element = document?.querySelector('.ra') as HTMLDivElement;
    if (element) {
      if (!submittedAvailabilities?.resident_availability?.length) {
        element?.classList?.add('has-footer');
      } else if (element?.classList?.contains('has-footer')) {
        element?.classList?.remove('has-footer');
      }
    }
  }, [submittedAvailabilities?.resident_availability?.length]);

  useEffect(() => {
    const element = document?.querySelector('.ra') as HTMLDivElement;
    if (element) {
      if (
        !submittedAvailabilities?.resident_availability?.length
        && Object.keys(errors?.resident_availabilities || {})?.length
      ) {
        element?.classList?.add('error-feedback');
      } else if (element?.classList?.contains('error-feedback')) {
        element?.classList?.remove('error-feedback');
      }
    }
  }, [
    errors?.resident_availabilities,
    submittedAvailabilities?.resident_availability?.length,
  ]);

  useEffect(() => {
    if (Object.keys(submittedAvailabilities || {})?.length > 0) {
      const defaultDates = generateDates();
      const sortedDates = [...(defaultDates || [])].sort((a, b) => new Date(a.date).getTime()
        - new Date(b.date).getTime());
      reset({ resident_availabilities: sortedDates });
    }
  }, [
    generateDates,
    reset,
    submittedAvailabilities,
  ]);

  if (submittedAvailabilities?.resident_availability?.length
    && !submittedAvailabilities?.is_editable) {
    return <AvailabilitySubmittedSuccess />;
  }

  let hasToday = false;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="availability-form">
        <div className="availability-selection" data-testid="availability-selection-container">
          <h3 className="h4 mb-12">Select Availability</h3>
          {controlledFields?.map((field, index) => {
            const dayValue = getDayValue(
              field.date,
              submittedAvailabilities?.timezone,
            );

            const dayLabel = (() => {
              if (dayValue === 'today') {
                hasToday = true;
                return 'Today';
              }
              if (dayValue === 'tomorrow' && !hasToday) {
                return 'Tomorrow';
              }
              return '';
            })();

            return (
              <Fragment key={field?.id}>
                <div className="availability-selection-item" data-testid="date-timing-row">
                  <div className="availability-selection-head">
                    <Label>
                      {moment(field?.date, 'MM-DD-YYYY').format('ddd, MMMM DD')}
                      {dayLabel ? (
                        <span>
                          {' '}
                          (
                          {dayLabel}
                          )
                        </span>
                      ) : null}

                    </Label>
                    <FormGroup check>
                      <InputField
                        name={`resident_availabilities.${index}.timings`}
                        type="checkbox"
                        id={`${field?.id}-anyTime`}
                        checked={hasAllChecked(field?.timings || [])}
                        customInputChangeHandler={(event) => onChangeAnytimeHandler(index, event)}
                      />
                      <Label check for={`${field?.id}-anyTime`}>
                        Anytime
                      </Label>
                    </FormGroup>
                  </div>
                  <div className="form-check-wrapper">
                    {timings?.map((timing) => (
                      <FormGroup
                        check
                        className="form-check-boxed"
                        key={`${field?.id}-${timing?.value}`}
                      >
                        <InputField
                          type="checkbox"
                          name={`resident_availabilities.${index}.timings`}
                          checked={field?.timings?.includes(timing?.value)}
                          customInputChangeHandler={(event) => onAvailabilityChangeHandler(
                            index,
                            event,
                            timing?.value,
                          )}
                          id={`${timing?.id}${index}`}
                        />
                        <Label for={`${timing?.id}${index}`} check>
                          <div className="day">{timing?.label}</div>
                          <div className="time">{timing?.time}</div>
                        </Label>
                      </FormGroup>
                    ))}
                  </div>
                </div>
                <div className="global-divider" />
              </Fragment>
            );
          })}
          <h5 className="h5 fw-500 mt-20 mb-8">Need to Schedule Further Out?</h5>

          {import.meta.env.VITE_PLATFORM === 'lula'
            ? (
              <a
                href={`tel:${import.meta.env.VITE_RESIDENT_HELP_LINE_NUMBER}`}
                className="btn secondary-button"
              >
                Call
                {' '}
                {import.meta.env.VITE_RESIDENT_HELP_LINE_NUMBER}
              </a>
            ) : (
              <h5 className="h5">
                Contact your maintenance coordinator for assistance.
              </h5>
            )}

        </div>
        <div className="ra-footer">
          <ErrorMessage
            errors={errors}
            name="resident_availabilities.root"
            render={({ message }) => <FormFeedback>{message}</FormFeedback>}
          />
          <Button
            className="primary-button btn-block btn btn-secondary"
            type="submit"
            disabled={isSubmitting}
          >
            Submit Availability
            {isSubmitting ? <div className="loader" /> : ''}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default SelectResidentAvailability;
